import * as React from 'react';

// WebOTP API isn't present in every TS lib.dom version — declare the bits we
// use so this compiles regardless. Merges with the built-in interface.
declare global {
  interface CredentialRequestOptions {
    otp?: { transport: string[] };
  }
}

/**
 * useWebOtp — auto-read the incoming SMS OTP via the WebOTP API.
 *
 * Supported on Chromium browsers on Android. When the user receives the SMS,
 * the browser surfaces a "verify" prompt; on confirm we get the code and the
 * caller fills (and usually submits) the form — no typing required.
 *
 * No-ops everywhere the API is missing (iOS Safari, desktop) — there, iOS's
 * native `autocomplete="one-time-code"` suggestion handles autofill instead.
 *
 * IMPORTANT: for the API to return a code, the SMS body must end with a line:
 *     @<your-domain> #<otp>
 * e.g. `@app.finminos.com #123456`. The domain must match the page origin.
 * That is a backend / SMS-template change (see Twilio Verify message config).
 *
 * The request is aborted on unmount/disable, so navigating away or submitting
 * cleanly cancels the pending prompt.
 */
export function useWebOtp(onCode: (code: string) => void, enabled = true): void {
  // Keep the latest callback without re-arming the listener each render.
  const onCodeRef = React.useRef(onCode);
  React.useEffect(() => {
    onCodeRef.current = onCode;
  }, [onCode]);

  React.useEffect(() => {
    if (!enabled) return;
    if (typeof window === 'undefined' || !('OTPCredential' in window)) return;

    const controller = new AbortController();

    navigator.credentials
      .get({ otp: { transport: ['sms'] }, signal: controller.signal })
      .then((credential) => {
        const code = (credential as { code?: string } | null)?.code;
        if (code) onCodeRef.current(code);
      })
      .catch(() => {
        // Aborted (unmount/submit) or no SMS detected — ignore silently.
      });

    return () => controller.abort();
  }, [enabled]);
}
